import api from '@/lib/api';
import type { SshKeyDto } from '@/api/hetzner';
import type { GithubRunnerScope, RunnerPlatform } from '@/api/runners';
import type { CloudProvider } from '@/lib/providers';

export type AnchorKind = 'MANAGED' | 'EXTERNAL';
export type ScalingMetric = 'FREE_JOB_SLOTS' | 'CPU' | 'MEMORY';
export type ScalingPreset = 'EAGER' | 'BALANCED' | 'CONSERVATIVE';
export type ScalingEventType =
  | 'SCALE_OUT'
  | 'SCALE_IN_STARTED'
  | 'BURST_DELETED'
  | 'FAILURE'
  | 'POLICY_AUTO_DISABLED'
  | 'SKIPPED'
  | 'CLEANUP_BLOCKED';

/** FREE_JOB_SLOTS rules have `maxFreeSlots`; CPU and MEMORY rules have the percentage thresholds. */
export interface ScalingRule {
  metric: ScalingMetric;
  durationMinutes: number;
  maxFreeSlots?: number;
  scaleOutThreshold?: number;
  scaleInThreshold?: number;
}

/** Job slots of the anchor and its ready burst runners from their newest reports. */
export interface JobCapacity {
  slots: number;
  busy: number;
  free: number;
}

/**
 * The burst template of an external runner, of the runner's platform; its tokens are never returned. A GitHub template
 * has `github`, and its `gitlabHost` and `executor` are empty.
 */
export interface BurstTemplate {
  productId: string;
  location: string;
  gitlabHost: string;
  executor: string;
  concurrency: number;
  sshKeyIds: string[];
  labels: string[];
  cacheId: string | null;
  metricsConnectionId: string | null;
  hetznerAuthTokenSet: boolean;
  runnerTokenSet: boolean;
  platform?: RunnerPlatform;
  github?: GithubRunnerScope & { tokenSet: boolean };
  /** Missing means Hetzner. */
  provider?: CloudProvider;
}

/** Blank or omitted tokens keep the stored ones. GitLab templates have the GitLab fields, GitHub ones `github`. */
export interface BurstTemplateRequest {
  productId: string;
  location: string;
  gitlabHost?: string;
  executor?: string;
  concurrency: number;
  sshKeyIds: string[];
  labels: string[];
  cacheId?: string;
  metricsConnectionId?: string;
  hetznerAuthToken?: string;
  /** Takes precedence over `hetznerAuthToken`; changing the provider needs a new token. */
  providerAuthToken?: string;
  /** Omitted keeps the stored provider, or Hetzner for a new template. */
  provider?: CloudProvider;
  runnerToken?: string;
  platform?: RunnerPlatform;
  github?: { owner: string; repository?: string; runnerGroup?: string; token?: string };
}

/** Where a managed runner's burst runners are created on another provider than the runner's. */
export interface BurstProvider {
  provider: CloudProvider;
  productId: string;
  location: string;
  sshKeyIds: string[];
}

/**
 * With the runner's provider, burst runners copy the runner and only `provider` is sent; with another one the other
 * fields are needed, except that an omitted token keeps the stored one of that provider.
 */
export interface BurstProviderRequest {
  provider: CloudProvider;
  productId?: string;
  location?: string;
  providerAuthToken?: string;
  sshKeyIds?: string[];
}

export interface ScalingEvent {
  at: string;
  type: ScalingEventType;
  runnerId: string | null;
  message: string;
}

export interface BurstRunnerStatus {
  runnerId: string;
  /** Null once the runner is gone. */
  name?: string | null;
  state: string | null;
  phase: 'BOOTING' | 'READY' | 'LEAVING';
  /** When it was reserved, before its server was requested. */
  created: string;
  readyAt: string | null;
  /** When Hetzner created its server, from which it is billed; null without a server. */
  startedAt?: string | null;
  /** Why its server and IP addresses cannot be deleted yet; it still counts against the maximum. */
  cleanupBlocked: string | null;
}

export interface ScalingPolicy {
  policyId: string;
  anchor: { runnerId: string; kind: AnchorKind; name: string; jobLimit: number | null };
  enabled: boolean;
  maxBurstRunners: number;
  stepSize: number;
  preset: ScalingPreset | null;
  rules: ScalingRule[];
  scaleInWindowMinutes: number;
  stabilizationMinutes: number;
  template?: BurstTemplate;
  /** Only while a managed runner's burst runners use another provider than the runner; tokens are never returned. */
  burst?: BurstProvider;
  status: {
    burstRunners: number;
    booting: number;
    draining: number;
    lastDecision: string | null;
    lastReason: string | null;
    lastDecisionAt: string | null;
    consecutiveFailures: number;
    backoffUntil: string | null;
    disabledReason: string | null;
    cleanupBlocked: number;
    runners: BurstRunnerStatus[];
    /** Null while the anchor's job slots or running jobs are unknown. */
    capacity: JobCapacity | null;
  };
  events: ScalingEvent[];
}

export interface ScalingPolicyRequest {
  enabled: boolean;
  maxBurstRunners: number;
  stepSize: number;
  preset?: ScalingPreset;
  rules?: ScalingRule[];
  scaleInWindowMinutes: number;
  stabilizationMinutes: number;
  template?: BurstTemplateRequest;
  /** Managed runners only; omitted keeps the stored provider of the burst runners, the runner's by default. */
  burst?: BurstProviderRequest;
}

/** Identifies the runner a policy belongs to. */
export interface ScalingAnchor {
  kind: AnchorKind;
  runnerId: string;
}

function policyUrl(anchor: ScalingAnchor) {
  return `${anchor.kind === 'EXTERNAL' ? 'external-runner' : 'runner'}/${anchor.runnerId}/scaling-policy`;
}

export async function getScalingPolicy(anchor: ScalingAnchor): Promise<ScalingPolicy | null> {
  try {
    const { data } = await api.get<ScalingPolicy>(policyUrl(anchor));
    return data;
  } catch (e) {
    const response = (e as { response?: { status?: number; data?: { code?: string } } } | null)
      ?.response;
    if (response?.status === 404 && response.data?.code === 'SCALING_POLICY_NOT_FOUND') return null;
    throw e;
  }
}

export async function saveScalingPolicy(
  anchor: ScalingAnchor,
  request: ScalingPolicyRequest
): Promise<ScalingPolicy> {
  const { data } = await api.put<ScalingPolicy>(policyUrl(anchor), request);
  return data;
}

export async function setScalingPolicyEnabled(
  anchor: ScalingAnchor,
  enabled: boolean
): Promise<ScalingPolicy> {
  const { data } = await api.post<ScalingPolicy>(
    `${policyUrl(anchor)}/${enabled ? 'enable' : 'disable'}`
  );
  return data;
}

export async function deleteScalingPolicy(anchor: ScalingAnchor): Promise<void> {
  await api.delete(policyUrl(anchor));
}

export async function listScalingEvents(anchor: ScalingAnchor): Promise<ScalingEvent[]> {
  const { data } = await api.get<{ events: ScalingEvent[] }>(`${policyUrl(anchor)}/events`);
  return data.events;
}

/** The SSH keys of the stored burst template's Hetzner project or provider account, listed with its stored token. */
export async function listTemplateSshKeys(anchor: ScalingAnchor): Promise<SshKeyDto[]> {
  const { data } = await api.get<SshKeyDto[]>(`${policyUrl(anchor)}/ssh-keys`);
  return Array.isArray(data) ? data : [];
}
